import { useState } from "react";
import CalculatorLayout from "@/components/CalculatorLayout";
import { ResultCard } from "@/components/ResultCard";
import AgeGenderFields from "@/components/AgeGenderFields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { addDays, formatDate, parseNum } from "@/calculators/_helpers";

const parseDate = (s) => (s ? new Date(s + "T00:00:00") : null);

// All Women's Health calculators show Age only (no gender selector — these tools are
// intended for female users, per the product spec).
function AgeOnly({ age, setAge }) {
  return <AgeGenderFields age={age} setAge={setAge} gender="female" setGender={() => {}} hideGender />;
}

// ---------- Pregnancy ----------
export function PregnancyCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [mode, setMode] = useState("lmp");
  const [dateStr, setDateStr] = useState("");
  const [cycle, setCycle] = useState("28");
  const d = parseDate(dateStr);
  let lmp = null;
  if (d) {
    if (mode === "lmp") lmp = d;
    else if (mode === "conception") lmp = addDays(d, -14);
    else if (mode === "due") lmp = addDays(d, -280);
  }
  const cyc = parseNum(cycle) || 28;
  const dueDate = lmp ? addDays(lmp, 280 + (cyc - 28)) : null;
  const today = new Date();
  const days = lmp ? Math.floor((today - lmp) / (1000 * 60 * 60 * 24)) : 0;
  const weeks = Math.floor(days / 7);
  const remDays = days - weeks * 7;
  const trimester = weeks < 13 ? "First" : weeks < 27 ? "Second" : "Third";
  const inWindow = lmp && weeks >= 0 && weeks <= 42;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Track pregnancy — current week, trimester and estimated due date."
      resultText={inWindow ? `You are ${weeks} weeks + ${remDays} days pregnant (${trimester} trimester). Due date: ${formatDate(dueDate)}.` : null}
      formula={<p>Standard model: 40 weeks (280 days) from last menstrual period (LMP). Conception is typically ~14 days after LMP for a 28-day cycle.</p>}
      faqs={[
        { q: "Which date should I use if I know both LMP and IVF transfer?", a: "Use the IVF transfer date option in a due-date calculator — it's more accurate than LMP." },
        { q: "How many trimesters are there?", a: "Three: 1st (weeks 1–12), 2nd (13–26), 3rd (27–40)." },
      ]}
    >
      <div className="space-y-4">
        <AgeOnly age={age} setAge={setAge} />
        <div className="space-y-2"><Label>I know my…</Label>
          <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-3 gap-2">
            {[["lmp", "LMP date"], ["conception", "Conception"], ["due", "Due date"]].map(([id, l]) => (
              <div key={id}>
                <RadioGroupItem value={id} id={`pm-${id}`} className="peer sr-only" />
                <Label htmlFor={`pm-${id}`} className="flex h-10 cursor-pointer items-center justify-center rounded-full border border-border text-xs sm:text-sm font-medium peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary">{l}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Date</Label><Input type="date" data-testid="preg-date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} /></div>
          {mode === "lmp" && <div className="space-y-2"><Label>Cycle length (days)</Label><Input type="number" data-testid="preg-cycle" value={cycle} onChange={(e) => setCycle(e.target.value)} /></div>}
        </div>
      </div>
      {inWindow && (
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <ResultCard label="Current week" value={`${weeks}w ${remDays}d`} testId="preg-week" />
          <ResultCard label="Trimester" value={trimester} tone="good" />
          <ResultCard label="Due date" value={formatDate(dueDate)} tone="warning" />
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- Due Date ----------
export function DueDateCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [mode, setMode] = useState("lmp");
  const [dateStr, setDateStr] = useState("");
  const d = parseDate(dateStr);
  let due = null;
  if (d) {
    if (mode === "lmp") due = addDays(d, 280);
    else if (mode === "conception") due = addDays(d, 266);
    else if (mode === "ivf3") due = addDays(d, 266 - 3);
    else if (mode === "ivf5") due = addDays(d, 266 - 5);
  }
  return (
    <CalculatorLayout
      calc={calc}
      intro="Estimated delivery date (Naegele's rule)."
      resultText={due ? `Estimated due date: ${formatDate(due)}.` : null}
      formula={<p>LMP + 280 days. For conception date, add 266 days. IVF transfers subtract the embryo age (3 or 5 days).</p>}
      faqs={[
        { q: "How accurate is EDD?", a: "Only about 4% of babies arrive on their exact due date. ±2 weeks is normal." },
        { q: "Is a scan more accurate?", a: "First-trimester ultrasound is the most accurate way to date a pregnancy." },
      ]}
    >
      <div className="space-y-4">
        <AgeOnly age={age} setAge={setAge} />
        <div className="space-y-2"><Label>I know my…</Label>
          <select data-testid="due-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
            <option value="lmp">LMP (last period date)</option>
            <option value="conception">Conception date</option>
            <option value="ivf3">IVF transfer — day 3 embryo</option>
            <option value="ivf5">IVF transfer — day 5 embryo</option>
          </select></div>
        <div className="space-y-2"><Label>Date</Label>
          <Input type="date" data-testid="due-date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} /></div>
      </div>
      {due && <div className="mt-6"><ResultCard label="Estimated due date" value={formatDate(due)} testId="due-result" tone="good" /></div>}
    </CalculatorLayout>
  );
}

// ---------- Pregnancy Conception ----------
export function PregnancyConceptionCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [mode, setMode] = useState("due");
  const [dateStr, setDateStr] = useState("");
  const d = parseDate(dateStr);
  const conception = d ? (mode === "due" ? addDays(d, -266) : addDays(d, 14)) : null;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Back-calculate your estimated date of conception."
      resultText={conception ? `Estimated conception date: ${formatDate(conception)}.` : null}
      formula={<p>Conception ≈ Due date − 266 days OR LMP + 14 days (for a 28-day cycle). Ranges of ±5 days are typical.</p>}
      faqs={[
        { q: "Can I know the exact day I conceived?", a: "Not usually — sperm can live 3–5 days, so conception can happen days after intercourse." },
        { q: "Does this account for irregular cycles?", a: "Not precisely. If your cycle isn't 28 days, add (your cycle length − 28) days to the estimate." },
      ]}
    >
      <div className="space-y-4">
        <AgeOnly age={age} setAge={setAge} />
        <div className="space-y-2"><Label>Start from</Label>
          <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-2 gap-2">
            {[["due", "Due date"], ["lmp", "LMP date"]].map(([id, l]) => (
              <div key={id}>
                <RadioGroupItem value={id} id={`pcm-${id}`} className="peer sr-only" />
                <Label htmlFor={`pcm-${id}`} className="flex h-10 cursor-pointer items-center justify-center rounded-full border border-border text-sm font-medium peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary">{l}</Label>
              </div>
            ))}
          </RadioGroup></div>
        <div className="space-y-2"><Label>Date</Label>
          <Input type="date" data-testid="conc-date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} /></div>
      </div>
      {conception && <div className="mt-6"><ResultCard label="Estimated conception date" value={formatDate(conception)} testId="conc-result" tone="good" /></div>}
    </CalculatorLayout>
  );
}

// ---------- Pregnancy Weight Gain ----------
export function PregnancyWeightGainCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [bmi, setBmi] = useState("");
  const [week, setWeek] = useState("");
  const b = parseNum(bmi), w = parseNum(week);
  const tables = {
    under: { label: "Underweight (BMI < 18.5)", total: [12.5, 18], weekly: [0.44, 0.58] },
    normal: { label: "Normal (BMI 18.5–24.9)", total: [11.5, 16], weekly: [0.35, 0.5] },
    over: { label: "Overweight (BMI 25–29.9)", total: [7, 11.5], weekly: [0.23, 0.33] },
    obese: { label: "Obese (BMI ≥ 30)", total: [5, 9], weekly: [0.17, 0.27] },
  };
  const key = b > 0 ? (b < 18.5 ? "under" : b < 25 ? "normal" : b < 30 ? "over" : "obese") : null;
  const t = key ? tables[key] : null;
  const suggested = t && w > 12 ? [t.weekly[0] * (w - 12) + 1.5, t.weekly[1] * (w - 12) + 2] : null;
  return (
    <CalculatorLayout
      calc={calc}
      intro="IOM-recommended pregnancy weight gain by pre-pregnancy BMI."
      resultText={t ? `Category: ${t.label}. Total gain ${t.total[0]}–${t.total[1]} kg.` : null}
      formula={<p>Institute of Medicine (IOM) guidelines: total gain and weekly gain in the 2nd/3rd trimester vary by pre-pregnancy BMI. Weeks 1–12 add ~0.5–2 kg total.</p>}
      faqs={[
        { q: "Is more weight better?", a: "No — gaining more than recommended raises risks of gestational diabetes, hypertension and difficult labour." },
        { q: "What about twins?", a: "Twin pregnancies have higher recommendations (typically 17–25 kg for normal BMI). Please consult your provider." },
      ]}
    >
      <div className="space-y-4">
        <AgeOnly age={age} setAge={setAge} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Pre-pregnancy BMI</Label>
            <Input type="number" step="0.1" data-testid="pwg-bmi" value={bmi} onChange={(e) => setBmi(e.target.value)} /></div>
          <div className="space-y-2"><Label>Current week</Label>
            <Input type="number" data-testid="pwg-week" value={week} onChange={(e) => setWeek(e.target.value)} /></div>
        </div>
      </div>
      {t && (
        <div className="mt-6 space-y-3">
          <ResultCard label="Recommended total gain" value={`${t.total[0]}–${t.total[1]}`} unit="kg" testId="pwg-total">{t.label}</ResultCard>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-border p-4"><div className="text-xs uppercase text-muted-foreground">Weekly gain (2nd–3rd trimester)</div><div className="mt-1 font-semibold">{t.weekly[0]}–{t.weekly[1]} kg/week</div></div>
            {suggested && <div className="rounded-xl border border-border p-4"><div className="text-xs uppercase text-muted-foreground">Expected gain to date</div><div className="mt-1 font-semibold">{suggested[0].toFixed(1)}–{suggested[1].toFixed(1)} kg</div></div>}
          </div>
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- Ovulation ----------
export function OvulationCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [lastPeriod, setLastPeriod] = useState("");
  const [cycle, setCycle] = useState("28");
  const d = parseDate(lastPeriod);
  const cyc = parseNum(cycle) || 28;
  const ovulation = d ? addDays(d, cyc - 14) : null;
  const fertileStart = ovulation ? addDays(ovulation, -5) : null;
  const fertileEnd = ovulation ? addDays(ovulation, 1) : null;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Predict your ovulation date and fertile window."
      resultText={ovulation ? `Ovulation ~${formatDate(ovulation)}. Fertile window ${formatDate(fertileStart)} – ${formatDate(fertileEnd)}.` : null}
      formula={<p>Ovulation ≈ next period date − 14 days = LMP + (cycle length − 14). Fertile window spans 5 days before through 1 day after ovulation (sperm live ~5 days).</p>}
      faqs={[
        { q: "How reliable is date-based prediction?", a: "Useful for regular cycles. Ovulation-predictor kits or basal body temperature give better accuracy." },
        { q: "Can I still get pregnant outside the window?", a: "Chances are lower but not zero — cycles vary." },
      ]}
    >
      <div className="space-y-4">
        <AgeOnly age={age} setAge={setAge} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Last period start date</Label>
            <Input type="date" data-testid="ov-date" value={lastPeriod} onChange={(e) => setLastPeriod(e.target.value)} /></div>
          <div className="space-y-2"><Label>Cycle length (days)</Label>
            <Input type="number" data-testid="ov-cycle" value={cycle} onChange={(e) => setCycle(e.target.value)} /></div>
        </div>
      </div>
      {ovulation && (
        <div className="mt-6 space-y-3">
          <ResultCard label="Predicted ovulation" value={formatDate(ovulation)} testId="ov-result" tone="good" />
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs uppercase text-muted-foreground">Fertile window</div>
            <div className="mt-1 font-semibold">{formatDate(fertileStart)} → {formatDate(fertileEnd)}</div>
          </div>
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- Conception (best days) ----------
export function ConceptionCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [lastPeriod, setLastPeriod] = useState("");
  const [cycle, setCycle] = useState("28");
  const d = parseDate(lastPeriod);
  const cyc = parseNum(cycle) || 28;
  const ovulation = d ? addDays(d, cyc - 14) : null;
  const days = ovulation ? [-5, -4, -3, -2, -1, 0, 1].map((n) => addDays(ovulation, n)) : [];
  return (
    <CalculatorLayout
      calc={calc}
      intro="Best days to try to conceive, based on your cycle."
      resultText={ovulation ? `Best conception window: ${formatDate(days[0])} – ${formatDate(days[6])}.` : null}
      formula={<p>Fertile window = 5 days before ovulation through 1 day after (sperm survive ~5 days; egg survives ~24 hours). The 2 days before ovulation are peak.</p>}
      faqs={[
        { q: "Which days have the highest chance?", a: "The 2 days immediately before ovulation." },
        { q: "How often should we try?", a: "Every 1–2 days during the fertile window gives the best odds without stress." },
      ]}
    >
      <div className="space-y-4">
        <AgeOnly age={age} setAge={setAge} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Last period start date</Label>
            <Input type="date" data-testid="cc-date" value={lastPeriod} onChange={(e) => setLastPeriod(e.target.value)} /></div>
          <div className="space-y-2"><Label>Cycle length (days)</Label>
            <Input type="number" data-testid="cc-cycle" value={cycle} onChange={(e) => setCycle(e.target.value)} /></div>
        </div>
      </div>
      {ovulation && (
        <div className="mt-6 space-y-3">
          <ResultCard label="Ovulation day" value={formatDate(ovulation)} testId="cc-result" tone="good" />
          <div className="rounded-2xl border border-border p-4">
            <h3 className="text-sm font-semibold mb-3">Best days to conceive</h3>
            <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              {days.map((day, i) => (
                <li key={i} className={`rounded-lg p-2 text-center ${i === 4 || i === 5 ? "bg-primary text-primary-foreground font-semibold" : "bg-secondary"}`}>
                  {formatDate(day)}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-muted-foreground">Highlighted days are peak fertility.</p>
          </div>
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- Period ----------
export function PeriodCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [lastPeriod, setLastPeriod] = useState("");
  const [cycle, setCycle] = useState("28");
  const [len, setLen] = useState("5");
  const d = parseDate(lastPeriod);
  const cyc = parseNum(cycle) || 28;
  const plen = parseNum(len) || 5;
  const next = d ? Array.from({ length: 6 }, (_, i) => addDays(d, cyc * (i + 1))) : [];
  return (
    <CalculatorLayout
      calc={calc}
      intro="Predict your next six periods."
      resultText={next.length ? `Next period: ${formatDate(next[0])}.` : null}
      formula={<p>Next period date = previous period date + cycle length, repeated. Period duration is shown for planning only — actual flow varies by month.</p>}
      faqs={[
        { q: "What's a normal cycle length?", a: "21–35 days for adults. Some variation month-to-month is normal." },
        { q: "Why is my period late?", a: "Common reasons: stress, weight change, illness, hormonal contraception, pregnancy. Check with a provider if worried." },
      ]}
    >
      <div className="space-y-4">
        <AgeOnly age={age} setAge={setAge} />
        <div className="space-y-2"><Label>Last period start date</Label>
          <Input type="date" data-testid="pc-date" value={lastPeriod} onChange={(e) => setLastPeriod(e.target.value)} /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Cycle length (days)</Label>
            <Input type="number" data-testid="pc-cycle" value={cycle} onChange={(e) => setCycle(e.target.value)} /></div>
          <div className="space-y-2"><Label>Period length (days)</Label>
            <Input type="number" data-testid="pc-len" value={len} onChange={(e) => setLen(e.target.value)} /></div>
        </div>
      </div>
      {next.length > 0 && (
        <div className="mt-6" data-testid="pc-result">
          <h3 className="text-sm font-semibold mb-3">Next six predicted periods</h3>
          <ul className="grid gap-2 sm:grid-cols-2">
            {next.map((n, i) => (
              <li key={i} className="rounded-xl border border-border p-3 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Cycle {i + 1}</span>
                <span className="font-medium">{formatDate(n)} – {formatDate(addDays(n, plen - 1))}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </CalculatorLayout>
  );
}
